/**
 * UUID v4 的连字符分段长度：8-4-4-4-12，共 36 个字符。
 * 手工拼装时按该结构切片，保证与原生实现输出格式一致。
 */
const UUID_HEX_LENGTH = 32;
/**
 * 判断任意值是否提供原生 UUID v4 生成能力。
 *
 * DOM 类型声明把 `crypto.randomUUID` 描述为必然存在，但浏览器只在安全上下文
 * （HTTPS、localhost、127.0.0.1）下暴露它，因此这里按运行时能力判断，
 * 而不是依赖类型声明。
 *
 * @param value 待探测的运行时对象。
 * @returns 该对象可直接调用 `randomUUID` 时返回 true。
 */
function isNativeUuidSource(value) {
    return (typeof value === 'object' &&
        value !== null &&
        typeof Reflect.get(value, 'randomUUID') === 'function');
}
/**
 * 判断任意值是否提供随机字节填充能力。
 *
 * @param value 待探测的运行时对象。
 * @returns 该对象可直接调用 `getRandomValues` 时返回 true。
 */
function isRandomValuesSource(value) {
    return (typeof value === 'object' &&
        value !== null &&
        typeof Reflect.get(value, 'getRandomValues') === 'function');
}
/**
 * 生成符合 RFC 4122 的 UUID v4 字符串，作为全应用持久化实体 ID 的唯一来源。
 *
 * 背景：浏览器原生 `crypto.randomUUID` 只在安全上下文（HTTPS、localhost、127.0.0.1）
 * 下暴露。当开发服务器或测试环境通过局域网 IP、普通 HTTP 域名访问时，`window.crypto`
 * 依然存在但 `randomUUID` 被浏览器移除，直接调用会抛出
 * `TypeError: crypto.randomUUID is not a function`，导致剧情树初始化、节点创建等
 * 全链路中断。因此这里按可用能力逐级降级，而不是依赖单一 API。
 *
 * @returns 小写连字符格式的 UUID v4 字符串。
 */
export function createId() {
    /** 当前环境的 Web Crypto 能力视图；旧浏览器、SSR 与非安全上下文下能力可能缺失。 */
    const webCrypto = globalThis.crypto;
    // 首选：安全上下文下的原生实现，随机性与性能最好。
    if (isNativeUuidSource(webCrypto)) {
        return webCrypto.randomUUID();
    }
    // 降级一：非安全上下文仍保留 getRandomValues，按 UUID v4 规范手工拼装。
    if (isRandomValuesSource(webCrypto)) {
        /** 16 字节随机源，第 6、8 字节需按规范覆写版本位与变体位。 */
        const bytes = webCrypto.getRandomValues(new Uint8Array(16));
        // 高 4 位固定为 0100，标识版本 4。
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        // 高 2 位固定为 10，标识 RFC 4122 变体。
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        /** 16 字节转为 32 位十六进制串，再按 8-4-4-4-12 分段。 */
        const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
        return [
            hex.slice(0, 8),
            hex.slice(8, 12),
            hex.slice(12, 16),
            hex.slice(16, 20),
            hex.slice(20, UUID_HEX_LENGTH),
        ].join('-');
    }
    // 降级二：极端环境兜底，随机性弱于上两级，仅保证创作流程不被 ID 生成阻断。
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        /** 0-15 的随机整数，用于填充单个十六进制位。 */
        const random = (Math.random() * 16) | 0;
        // 变体位只能取 8/9/a/b，其余位直接使用随机值。
        const value = char === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
}
